import React, { useContext, useMemo, useState } from "react";
import { MenuItem, Popover, Typography, useTheme } from "@mui/material";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import { categoryService } from "../../services/category";
import { setTrackingState } from "../../store/actions/trackerAction";
import { UserTypeContext } from "../../context/userStatus";

interface MoveMetricPopoverProps {
  sourceCategoryId: string;
  metricType: "outcome" | "influence" | "reflective";
  metricId: string;
  journeyCategoryList: any[];
  token: string;
  restricted?: boolean;
  onRestricted?: () => void;
  callservice?: (isLast: boolean) => void;
  trackingDispatch?: (action: any) => void;
  // When provided, the move is performed against local state only (no API call).
  // Used by flows where the actual persistence happens later via a Finish/save button.
  onLocalMove?: (
    sourceCategoryId: string,
    targetCategoryId: string,
    metricType: "outcome" | "influence" | "reflective",
    metricId: string
  ) => void;
  /** Optional icon styles; omit on desktop to keep default appearance. */
  iconSx?: Record<string, unknown>;
}

const MoveMetricPopover = ({
  sourceCategoryId,
  metricType,
  metricId,
  journeyCategoryList,
  token,
  restricted = false,
  onRestricted,
  callservice,
  trackingDispatch,
  onLocalMove,
  iconSx,
}: MoveMetricPopoverProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const { state: IUserTypeState } = useContext(UserTypeContext);
  const isDarkMode = useTheme().palette.mode === "dark" || IUserTypeState.isDarkMode;
  const sourceKey = String(sourceCategoryId ?? "");

  const moveOptions = useMemo(
    () =>
      (journeyCategoryList || []).filter((category) => {
        if (!category || category?.name === "Other") return false;
        const id = category?.categoryId ?? category?._id;
        return String(id ?? "") !== sourceKey && Boolean(id);
      }),
    [journeyCategoryList, sourceKey]
  );

  if (!moveOptions.length) {
    return (
      <span
        title="Add another category to move metrics between categories"
        style={{
          display: "inline-flex",
          alignItems: "center",
          height: "100%",
          cursor: "default",
          pointerEvents: "none",
        }}
      >
        <SwapHorizIcon
          fontSize="small"
          sx={{ color: "var(--color-text-secondary)", opacity: 0.75, ...iconSx }}
        />
      </span>
    );
  }

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    if (restricted) {
      onRestricted && onRestricted();
      return;
    }
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const moveMetric = async (targetCategoryId: string) => {
    if (isLoading) return;

    if (onLocalMove) {
      try {
        onLocalMove(sourceCategoryId, targetCategoryId, metricType, metricId);
        trackingDispatch?.(
          setTrackingState({
            showSnakbar: true,
            snakbarMsg: "Metric moved",
            msgType: "success",
          })
        );
      } finally {
        handleClose();
      }
      return;
    }

    setIsLoading(true);
    try {
      const response = await categoryService.moveMetric(token, {
        sourceCategoryId,
        targetCategoryId,
        metricType,
        metricId,
      });
      callservice && callservice(false);
      trackingDispatch?.(
        setTrackingState({
          showSnakbar: true,
          snakbarMsg: response?.message || "Metric moved successfully",
          msgType: "success",
        })
      );
    } catch (error: any) {
      trackingDispatch?.(
        setTrackingState({
          showSnakbar: true,
          snakbarMsg:
            error?.response?.data?.description ||
            error?.response?.data?.message ||
            error?.message ||
            "Unable to move metric",
          msgType: "error",
        })
      );
    } finally {
      setIsLoading(false);
      handleClose();
    }
  };

  return (
    <>
      <span
        onClick={handleOpen}
        style={{
          display: "flex",
          alignItems: "center",
          height: "100%",
          cursor: isLoading ? "not-allowed" : "pointer",
          opacity: isLoading ? 0.5 : 1,
        }}
      >
        <SwapHorizIcon
          fontSize="small"
          sx={{ color: "var(--color-text-primary)", ...iconSx }}
        />
      </span>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        sx={{
          "& .MuiPaper-root": {
            borderRadius: "12px",
            border: isDarkMode ? "1px solid var(--dark-border-color)" : "1px solid #F3F3F3",
            minWidth: "200px",
            padding: "8px",
            background: isDarkMode ? "var(--dark-background)" : "#FFF",
          },
        }}
      >
        <Typography
          sx={{
            fontSize: "12px !important",
            fontWeight: 800,
            lineHeight: "16px",
            color: isDarkMode ? "var(--dark-text-primary)" : "var(--color-text-secondary)",
            padding: "8px 8px",
          }}
        >
          Move to
        </Typography>
        {moveOptions.map((category) => {
          const targetId = category.categoryId ?? category._id;
          return (
          <MenuItem
            key={targetId}
            onClick={() => moveMetric(String(targetId))}
            sx={{
              borderRadius: "0px",
              minHeight: "24px",
              fontSize: "12px !important",
              padding: "12px !important",
              background: "transparent !important",
              "&:hover": {
                background: "transparent !important",
              },
              "&:active": {
                background: "transparent !important",
              },
              "&:focus": {
                background: "transparent !important",
              },
              "&:focus-visible": {
                background: "transparent !important",
              },
              ...(isDarkMode ? {
                "&.MuiButtonBase-root": {
                  color: "var(--dark-text-primary) !important",
                },
              } : {}),
            }}
          >
            {category.name}
          </MenuItem>
          );
        })}
      </Popover>
    </>
  );
};

export default MoveMetricPopover;
